<!-- Search results: items, sets and progressions in one list, each with its patient and tags. -->
<template>
  <div class="md-results">
    <div v-if="hint" class="md-banner">{{ hint }}</div>
    <div class="md-chips md-results-chips">
      <button v-for="k in kinds" :key="k.key" class="md-chip" :class="{ on: kind === k.key }" @click="kind = k.key">
        {{ k.label }} <span class="n">({{ count(k.key) }})</span>
      </button>
    </div>
    <p v-if="!shown.length" class="md-muted">No media matches. Words must match whole; end one with * to match the start of a word.</p>
    <ul class="md-results-list">
      <li v-for="r in shown" :key="r.type + r.id">
        <button class="md-results-row" @click="$emit('open', r)">
          <span class="md-results-thumb"><MediaThumb :item="r.cover" ratio="1 / 1" /></span>
          <span class="md-results-main">
            <strong>{{ r.title }}</strong>
            <span class="md-small md-muted">{{ r.patient_name }} · {{ typeLabel(r) }} · {{ fmt(r.date) }}</span>
          </span>
          <span class="md-results-tags">
            <span v-if="r.primary_tag" class="md-tag primary">{{ store.primaryTagLabel(r.primary_tag) }}</span>
            <span v-for="t in (r.tags || []).slice(0, 2)" :key="t" class="md-tag">{{ t }}</span>
          </span>
        </button>
      </li>
    </ul>
    <p v-if="total > results.length" class="md-small md-muted">Showing the first {{ results.length }} of {{ total }}.</p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMediaStore } from '@/stores/mediaStore';
import MediaThumb from './MediaThumb.vue';

const props = defineProps({ results: { type: Array, required: true }, total: { type: Number, default: 0 }, hint: { type: String, default: null } });
defineEmits(['open']);
const store = useMediaStore();
const kind = ref('all');
const kinds = [{ key: 'all', label: 'All' }, { key: 'image', label: 'Images' }, { key: 'video', label: 'Videos' }, { key: 'set', label: 'Sets' }, { key: 'progression', label: 'Progressions' }];
const matches = (r, k) => k === 'all' || (k === 'set' || k === 'progression' ? r.type === k : r.type === 'item' && r.kind === k);
const count = (k) => props.results.filter((r) => matches(r, k)).length;
const shown = computed(() => props.results.filter((r) => matches(r, kind.value)));
const typeLabel = (r) => (r.type === 'item' ? (r.kind === 'video' ? 'Video' : 'Image') : r.type === 'set' ? `Set · ${r.count} items` : `Progression · ${r.count} sets`);
const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');
</script>

<style scoped>
.md-results-chips { margin-bottom:12px; }
.md-results-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
.md-results-row { width:100%; display:flex; align-items:center; gap:12px; background:#fff; border:1px solid var(--line); border-radius:10px; padding:8px; cursor:pointer; text-align:left; color:var(--ink); }
.md-results-row:hover { border-color:var(--teal); }
.md-results-thumb { flex:0 0 64px; border-radius:8px; overflow:hidden; }
.md-results-main { flex:1 1 auto; display:flex; flex-direction:column; min-width:0; }
.md-results-main strong { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.md-results-tags { flex:0 1 auto; text-align:right; }
@media (max-width: 640px) { .md-results-tags { display:none; } }
</style>
